import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * Payouts — plan-005 OP-38 Task 1 (FR-MNY-5, FR-MNY-6, FR-MNY-8; AC-MNY-3 and AC-MNY-5, UI half;
 * design §18.8.4). A surface the prototype does not draw: registered in `docs/design/exclusions.md`
 * under "Surfaces the design does not draw", and laid out with Billing's own classes and tokens so
 * the two money pages read as one family (ADR 0018).
 *
 * WHAT IS ON IT, top to bottom: the online-payment status (mode, what is missing, the terms); the
 * book — the live book WeTalk pays out, or, only when it has entries, the sandbox book of test
 * payments, marked as never paid out; the balance, held and available; what holds the next payout
 * and why; the next payout; the paid orders still inside the holding period; the period's
 * statement (every order line reads gross · WeTalk fee absorbed · commission 0 · net = gross) with
 * its CSV; refunds; disputes; payouts; and the payout details.
 *
 * WHO (OD-P5): Owners, Admins and Viewers read the statement and download it; Members do not, and
 * the sidebar leaves the page out for them. The payout details are Owner and Admin reading, Owner
 * changing (`payout-destination-panel.tsx`).
 *
 * NOTHING IS A FLOAT and nothing is summed here: the balance, the opening and closing balances and
 * each row's running balance are the API's (`balance_after_minor`, written under the ledger lock),
 * so `opening + entries = closing` is shown as the API computed it, never re-added in the browser.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ApiError } from "@wetalk/api-client";
import type { ProviderMode } from "@wetalk/domain";
import {
  Button,
  Icon,
  Segmented,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  Tag,
} from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { useGateway } from "../../api/provider.js";
import { apiProblemText } from "../../api/api-error.js";
import { authSessionQuery } from "../../api/query.js";
import {
  DISPUTE_STATE_LABEL,
  PAYABLE_ENTRY_KIND_LABEL,
  PAYOUT_STATE_LABEL,
  PAYOUT_STATE_TONE,
  PAYOUT_STATEMENT_ROLE,
  REFUND_STATE_LABEL,
  maskedIbanText,
  mayReadPayoutStatement,
  payoutHoldText,
  type PayableStatementEntryView,
  type PayoutStatementView,
} from "../../api/slice/payout.js";
import { GLYPH } from "../../shell/glyph.js";
import { rolesInPlainWords } from "../../shell/member-capability.js";
import { formatMoney } from "../../money.js";
import { FinanceKicker, FinanceTabs } from "../finance/finance-tabs.js";
import { OnlinePaymentStatusBody } from "./online-payment-status.js";
import { PayoutDestinationPanel } from "./payout-destination-panel.js";
import {
  PAYOUT_PERIOD,
  athensDateText,
  athensDateTimeText,
  dayText,
  payoutCsvFileName,
  payoutPeriodOf,
  saveCsv,
  signedMoneyText,
  type PayoutPeriod,
} from "./payout-copy.js";
import { onlinePaymentStatusQuery, payoutStatementQuery } from "./payout-query.js";

import "../billing/billing.css";
import "./payouts.css";

const BOOK_OPTION: readonly { readonly value: ProviderMode; readonly label: string }[] = [
  { value: "live", get label() { return portalText("finance", "m0806"); } },
  { value: "sandbox", get label() { return portalText("finance", "m0807"); } },
];

const periodOption = () => PAYOUT_PERIOD.map((one) => ({ value: one, label: { "This month": portalText("finance", "m0182"), "Last month": portalText("finance", "m1198"), "Last 90 days": portalText("finance", "m1255") }[one] }));

/** The sentence a statement read or export failure shows; `export_too_large` names what to do. */
function statementProblemText(error: unknown): string {
  if (error instanceof ApiError && error.code === "export_too_large") {
    return portalText("finance", "m0808");
  }
  return apiProblemText(error, portalText("finance", "m0809"));
}

/** "Order paid online · PZ-4A12", or an adjustment's stated reason. */
function entryDescriptionOf(one: PayableStatementEntryView): string {
  const label = PAYABLE_ENTRY_KIND_LABEL[one.kind];
  if (one.kind === "adjustment" && one.reason !== null) return `${label}: ${one.reason}`;
  return label;
}

function moneyOrDash(amount: PayableStatementEntryView["gross"]): string {
  return amount === null ? "—" : formatMoney(amount);
}

export function PayoutsScreen(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const authSession = useQuery(authSessionQuery(gateway));
  const role = authSession.data?.memberRole ?? null;
  const readable = mayReadPayoutStatement(role);
  const [book, setBook] = useState<ProviderMode>("live");
  const [period, setPeriod] = useState<PayoutPeriod>("This month");
  const range = payoutPeriodOf(period, new Date());
  const query = { from: range.from, to: range.to, providerMode: book };

  const status = useQuery(onlinePaymentStatusQuery(gateway));
  const statement = useQuery({ ...payoutStatementQuery(gateway, query), enabled: readable });
  const exported = useMutation({
    mutationFn: () => gateway.exportPayoutStatement(query),
    onSuccess: (text) => {
      saveCsv(text, payoutCsvFileName(book, range.from, range.to));
    },
  });

  const data = statement.data;
  // The sandbox view is offered only while the sandbox book has an entry (design §18.8.4).
  const sandboxOffered = data?.sandboxBookHasEntries === true || book === "sandbox";

  return (
    <section className="c-page bl-page po-page">
      {/* Design sync 2026-10-09: Payouts is a Finance tab — the payer kicker and the Finance strip above the headline. */}
      <div className="po-finance-head">
        <FinanceKicker className="bl-finance-kicker" />
        <FinanceTabs current="payouts" className="bl-finance-tabs" />
      </div>
      <div className="bl-headline">
        <div>
          <p className="bl-headline-kicker">{authSession.data?.accountName ?? portalText("finance", "m0810")}{portalText("finance", "m0811")}</p>
          <h1 className="bl-title">{portalText("finance", "m0812")}</h1>
        </div>
        {readable ? (
          <div className="bl-actions">
            <Button
              variant="secondary"
              disabled={data === undefined || exported.isPending}
              onClick={() => {
                exported.mutate();
              }}
            >
              <Icon glyph={GLYPH.downloadSimple} size="md" />
              <span>{exported.isPending ? portalText("finance", "m0813") : portalText("finance", "m0287")}</span>
            </Button>
          </div>
        ) : null}
      </div>
      {exported.isError ? (
        <p className="po-problem" role="alert">
          {statementProblemText(exported.error)}
        </p>
      ) : null}

      <div className="po-status po-status-page" role="group" aria-label={portalText("finance", "m0765")}>
        {status.data !== undefined ? (
          <OnlinePaymentStatusBody status={status.data} linkToPayouts={false} />
        ) : status.isError ? (
          <span className="po-status-headline" role="alert">
            {apiProblemText(status.error, portalText("finance", "m0766"))}
          </span>
        ) : (
          <span className="po-status-headline" role="status">{portalText("finance", "m0767")}</span>
        )}
      </div>

      {role !== null && !readable ? (
        <p className="c-note">{portalText("finance", "m0777")}{rolesInPlainWords(PAYOUT_STATEMENT_ROLE)}{portalText("finance", "m0814")}</p>
      ) : null}

      {readable ? (
        <>
          <div className="po-controls">
            {sandboxOffered ? (
              <Segmented name="payoutBook" legend={portalText("finance", "m1188")} options={BOOK_OPTION} value={book} onChange={setBook} />
            ) : null}
            <Segmented name="payoutPeriod" legend={portalText("finance", "m0443")} options={periodOption()} value={period} onChange={setPeriod} />
            <span className="po-muted">
              {dayText(range.from)} – {dayText(range.to)}{portalText("finance", "m0815")}</span>
          </div>

          {book === "sandbox" ? (
            <p className="po-notice po-notice-sandbox" role="status">
              <Tag tone="outline">{portalText("finance", "m0807")}</Tag>
              <span>{portalText("finance", "m0816")}</span>
            </p>
          ) : null}

          {statement.isError ? (
            <p className="po-problem" role="alert">
              {statementProblemText(statement.error)}
            </p>
          ) : data === undefined ? (
            <p className="c-note" role="status">{portalText("finance", "m0817")}</p>
          ) : (
            <StatementBody statement={data} />
          )}
        </>
      ) : null}

      <PayoutDestinationPanel role={role} changeHoldDay={status.data === undefined ? null : status.data.destinationChangeHoldDay} />
    </section>
  );
}

function StatementBody({ statement }: { readonly statement: PayoutStatementView }): ReactNode {
  usePortalTranslation("finance");
  const paidOut = statement.providerMode === "live";
  const next = statement.nextPayout;
  const minimumText = next === null ? "" : formatMoney(next.minimum);
  return (
    <>
      <div className="bl-stats po-stats" title={portalText("finance", "m0818", { value1: athensDateTimeText(statement.asOf) })}>
        <div className="bl-stat">
          <span className="bl-stat-label">{portalText("finance", "m0819")}</span>
          <span className="bl-stat-value">{formatMoney(statement.balance)}</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-label">{portalText("finance", "m0820")}</span>
          <span className="bl-stat-value">{formatMoney(statement.held)}</span>
        </div>
        <div className="bl-stat">
          <span className="bl-stat-label">{portalText("finance", "m0821")}</span>
          <span className="bl-stat-value">{formatMoney(statement.available)}</span>
        </div>
        {paidOut && next !== null ? (
          <div className="bl-stat">
            <span className="bl-stat-label">{portalText("finance", "m0822")}{athensDateText(next.cutoffAt)}</span>
            <span className="bl-stat-value">{formatMoney(next.expected)}</span>
          </div>
        ) : null}
      </div>

      {statement.available.amountMinor < 0 ? (
        <p className="po-notice" role="status">{portalText("finance", "m0823")}</p>
      ) : null}

      {paidOut && statement.hold.length > 0 ? (
        <section className="po-section" aria-labelledby="po-hold-title">
          <h2 id="po-hold-title" className="po-section-title">{portalText("finance", "m0824")}</h2>
          <ul className="po-hold">
            {statement.hold.map((one) => (
              <li key={one.reason} className="po-hold-item">
                <Icon glyph={GLYPH.warning} size="md" tone="caution" />
                <span>{payoutHoldText(one, minimumText, athensDateText)}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {statement.heldCredit.length === 0 ? null : (
        <section className="po-section" aria-labelledby="po-held-title">
          <h2 id="po-held-title" className="po-section-title">{portalText("finance", "m0825")}</h2>
          <div className="bl-scroll">
            <Table caption={portalText("finance", "m0826")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("finance", "m0827")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0828")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0110")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statement.heldCredit.map((one) => (
                  <TableRow key={one.payableEntryId}>
                    <TableCell>{one.customerOrderReference ?? "—"}</TableCell>
                    <TableCell>{athensDateText(one.releaseAt)}</TableCell>
                    <TableCell numeric>{formatMoney(one.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      <section className="po-section" aria-labelledby="po-statement-title">
        <h2 id="po-statement-title" className="po-section-title">{portalText("finance", "m0829")}</h2>
        <p className="po-muted">{portalText("finance", "m0830")}</p>
        {statement.entry.length === 0 ? (
          <p className="c-note">{portalText("finance", "m0831")}</p>
        ) : (
          <div className="bl-scroll">
            <Table caption={portalText("finance", "m0832")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("finance", "m0294")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0295")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0827")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0833")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0834")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0835")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0836")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0298")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statement.entry.map((one) => (
                  <TableRow key={one.payableEntryId}>
                    <TableCell>{athensDateText(one.occurredAt)}</TableCell>
                    <TableCell>{entryDescriptionOf(one)}</TableCell>
                    <TableCell>{one.customerOrderReference ?? "—"}</TableCell>
                    <TableCell numeric>{moneyOrDash(one.gross)}</TableCell>
                    <TableCell numeric>{moneyOrDash(one.weTalkFeeAbsorbed)}</TableCell>
                    <TableCell numeric>{moneyOrDash(one.commission)}</TableCell>
                    <TableCell numeric>{signedMoneyText(one.net ?? one.amount)}</TableCell>
                    <TableCell numeric>{formatMoney(one.balanceAfter)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <dl className="po-totals">
          <div className="po-total">
            <dt>{portalText("finance", "m0837")}</dt>
            <dd>{formatMoney(statement.openingBalance)}</dd>
          </div>
          <div className="po-total">
            <dt>{portalText("finance", "m0838")}</dt>
            <dd>{signedMoneyText(statement.entryTotal)}</dd>
          </div>
          <div className="po-total">
            <dt>{portalText("finance", "m0839")}</dt>
            <dd>{formatMoney(statement.closingBalance)}</dd>
          </div>
        </dl>
      </section>

      {statement.refund.length === 0 ? null : (
        <section className="po-section" aria-labelledby="po-refund-title">
          <h2 id="po-refund-title" className="po-section-title">{portalText("finance", "m0840")}</h2>
          <div className="bl-scroll">
            <Table caption={portalText("finance", "m0841")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("finance", "m0842")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0827")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0579")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0444")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0110")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statement.refund.map((one) => (
                  <TableRow key={one.customerOrderRefundId}>
                    <TableCell>{athensDateText(one.createdAt)}</TableCell>
                    <TableCell>{one.customerOrderReference}</TableCell>
                    <TableCell>{one.reason}</TableCell>
                    <TableCell>{REFUND_STATE_LABEL[one.state]}</TableCell>
                    <TableCell numeric>{formatMoney(one.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      {statement.dispute.length === 0 ? null : (
        <section className="po-section" aria-labelledby="po-dispute-title">
          <h2 id="po-dispute-title" className="po-section-title">{portalText("finance", "m0843")}</h2>
          <p className="po-muted">{portalText("finance", "m0844")}</p>
          <div className="bl-scroll">
            <Table caption={portalText("finance", "m0845")}>
              <TableHead>
                <TableRow>
                  <TableHeaderCell>{portalText("finance", "m0846")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0827")}</TableHeaderCell>
                  <TableHeaderCell>{portalText("finance", "m0444")}</TableHeaderCell>
                  <TableHeaderCell numeric>{portalText("finance", "m0110")}</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statement.dispute.map((one) => (
                  <TableRow key={one.customerOrderDisputeId}>
                    <TableCell>{athensDateText(one.detectedAt)}</TableCell>
                    <TableCell>{one.customerOrderReference}</TableCell>
                    <TableCell>{DISPUTE_STATE_LABEL[one.state]}</TableCell>
                    <TableCell numeric>{formatMoney(one.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </section>
      )}

      {paidOut ? (
        <section className="po-section" aria-labelledby="po-payout-title">
          <h2 id="po-payout-title" className="po-section-title">{portalText("finance", "m0847")}</h2>
          {statement.payout.length === 0 ? (
            <p className="c-note">{portalText("finance", "m0848")}</p>
          ) : (
            <div className="bl-scroll">
              <Table caption={portalText("finance", "m0849")}>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>{portalText("finance", "m0850")}</TableHeaderCell>
                    <TableHeaderCell>{portalText("finance", "m0444")}</TableHeaderCell>
                    <TableHeaderCell>{portalText("finance", "m0851")}</TableHeaderCell>
                    <TableHeaderCell>{portalText("finance", "m0430")}</TableHeaderCell>
                    <TableHeaderCell>{portalText("finance", "m0852")}</TableHeaderCell>
                    <TableHeaderCell>{portalText("finance", "m0577")}</TableHeaderCell>
                    <TableHeaderCell numeric>{portalText("finance", "m0110")}</TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {statement.payout.map((one) => (
                    <TableRow key={one.payoutId}>
                      <TableCell>{athensDateText(one.cutoffAt)}</TableCell>
                      <TableCell>
                        <Tag status={PAYOUT_STATE_TONE[one.state]}>{PAYOUT_STATE_LABEL[one.state]}</Tag>
                        {one.failureReason === null ? null : <span className="po-muted"> {one.failureReason}</span>}
                      </TableCell>
                      <TableCell>{one.sentAt === null ? "—" : athensDateText(one.sentAt)}</TableCell>
                      <TableCell>{one.settledAt === null ? "—" : athensDateText(one.settledAt)}</TableCell>
                      <TableCell>{one.bankReference ?? "—"}</TableCell>
                      <TableCell>{one.ibanLastFour === null ? "—" : maskedIbanText(one.ibanLastFour)}</TableCell>
                      <TableCell numeric>{formatMoney(one.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </section>
      ) : null}
    </>
  );
}
